// @owners { team: patients-team }
import {
  ActionSheetContext,
  Button,
  Card,
  Link,
  LoadingEmptyState,
  SecondaryPage,
  Toast,
  ToastContext,
  XlPadding,
} from '@alto/design-system';
import { BenefitInputActionSheet, useInsuranceWarningActionSheet, useQueryInsuranceConfigs } from '@alto/features';
import { useNavigation } from '@alto/navigation';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useContext, useEffect } from 'react';
import type { FieldErrors } from 'react-hook-form';
import { useForm } from 'react-hook-form';
// eslint-disable-next-line import/no-deprecated
import { clearInsuranceError, createInsurance, updateInsurance } from '~shared/actions/insurances';
import { setEditingInsuranceID } from '~shared/actions/onboarding';
import { getVerifyFailureErrorMessage } from '~shared/constants';
import { getInferredInsuranceFormValues } from '~shared/features/insurances/helpers/getInferredInsuranceFormValues';
import {
  type Values,
  getInitialInsuranceFormValues,
} from '~shared/features/insurances/helpers/getInitialInsuranceFormValues';
import { removeEmptyInsuranceValues } from '~shared/features/insurances/helpers/removeEmptyInsuranceValues';
import { INSURANCE_INPUT_MANUAL, MEDICAL_INFO } from '~shared/features/onboarding/helpers/onboardingRouteHandler';
import { ROUTE_TO_URL, routeFromPath } from '~shared/features/onboarding/helpers/routeToPath';
import { getEditingOnboardingInsuranceID } from '~shared/features/onboarding/selectors/getOnboarding';
import getLoading from '~shared/features/ui/selectors/getLoading';
import { getCurrentUser } from '~shared/features/users/selectors/getCurrentUser';
import { getIsProgynyUser } from '~shared/features/users/selectors/getIsProgynyUser';
import { getSelectedUser } from '~shared/features/users/selectors/getSelectedUser';
import { getErrorMessage } from '~shared/helpers/helper';
import { useAnalytics } from '~shared/hooks/useAnalytics';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { queries } from '~shared/queries/query-keys';
import { useGetInsuranceByID } from '~shared/queries/query-keys/insurances';
import { getNotRestrictedPartnerError } from '~shared/selectors/ui/errors/getInsuranceError';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { type Insurance } from '~shared/types/clients';
import { useNavigationContext } from '~web/contexts';
import { InsuranceFormFields, validate } from '~web/features/onboarding/components/redesign/InsuranceFormFields';
import { OnboardingHeader } from '~web/features/onboarding/components/redesign/OnboardingHeader';
import { OnboardingNavBarLogo } from '~web/features/onboarding/components/redesign/OnboardingNavBarLogo';
import { nextUrl as getNextUrl, previousUrl as getPreviousUrl } from '~web/features/onboarding/helpers/urlHandler';

const OnboardingInsuranceInputManual = () => {
  const { navigate } = useNavigation();
  const queryClient = useQueryClient();
  const dispatch = useDispatchShared();
  const { trackEvent, trackPageView } = useAnalytics();
  const { addToast } = useContext(ToastContext);
  const { setActiveActionSheet } = useContext(ActionSheetContext);

  const loading = useSelectorShared(getLoading);
  const insuranceID = useSelectorShared(getEditingOnboardingInsuranceID);
  const { previous: previousWebUrl } = useNavigationContext();
  const origin = previousWebUrl ? routeFromPath(previousWebUrl) : '';
  const nextUrl = getNextUrl({ currentRoute: INSURANCE_INPUT_MANUAL });
  const previousUrl = getPreviousUrl({
    params: { editingInsuranceID: insuranceID },
    currentRoute: INSURANCE_INPUT_MANUAL,
  });

  const selectedUser = useSelectorShared(getSelectedUser);
  const currentUser = useSelectorShared(getCurrentUser);
  const user = selectedUser || currentUser;
  const isProgyny = useSelectorShared((state) => getIsProgynyUser(state, { userID: user?.id }));
  const { insurance: savedOnboardingInsurance } = useGetInsuranceByID(insuranceID);

  const title = isProgyny ? 'Enter your insurance' : 'Enter benefits details';
  const error = useSelectorShared(getNotRestrictedPartnerError);
  const addBenefitButtonText = isProgyny ? 'Save insurance' : 'Save benefit';
  const saving = loading.createInsuranceLoading || loading.updateInsuranceLoading;
  const { headerBackgroundColor, headerLogoUrl, isFetching, partnershipName } = useQueryInsuranceConfigs();

  const openInsuranceInfoActionSheet = () => {
    setActiveActionSheet(<BenefitInputActionSheet />);
  };

  const afterSave = (newInsurance: Insurance) => {
    if (!newInsurance) {
      addToast(<Toast variant="error">{getErrorMessage(error) || getVerifyFailureErrorMessage()}</Toast>);
      return;
    }
    queryClient.invalidateQueries({ queryKey: queries.insurances.fetchAll._def });
    dispatch(setEditingInsuranceID(null));
    navigate(nextUrl);

    if (!savedOnboardingInsurance) {
      trackEvent({
        event: EVENTS.INSURANCE_ADDED,
        params: {
          type: 'manual',
          source: 'onboarding',
          entity_name: newInsurance.insurance_plan_name,
        },
      });
    } else {
      queryClient.invalidateQueries({
        queryKey: queries.insurances.fetchOne({ insuranceID: savedOnboardingInsurance.id }).queryKey,
      });
    }
  };

  const onSave = async (values: Values) => {
    if (user) {
      const newInsuranceValues = getInferredInsuranceFormValues(values, user);
      if (savedOnboardingInsurance) {
        return dispatch(
          // eslint-disable-next-line import/no-deprecated
          updateInsurance({
            id: savedOnboardingInsurance.id,
            ...removeEmptyInsuranceValues(newInsuranceValues),
          }),
        ).then(afterSave);
      } else {
        return dispatch(
          // eslint-disable-next-line import/no-deprecated
          createInsurance({
            user_id: user.id,
            ...removeEmptyInsuranceValues(newInsuranceValues),
          }),
        ).then(afterSave);
      }
    }
  };

  const onBack = useCallback(() => {
    if (previousUrl) {
      navigate(previousUrl);
    }
  }, [navigate, previousUrl]);

  const onCancel = useCallback(() => {
    dispatch(setEditingInsuranceID(null));
    onBack();
  }, [dispatch, onBack]);

  const insuranceWarningOnContinue = useCallback(() => {
    navigate(ROUTE_TO_URL[MEDICAL_INFO]);
    dispatch(clearInsuranceError());
    dispatch(setEditingInsuranceID(null));
  }, [dispatch, navigate]);

  const insuranceWarningOnAddAnotherBenefit = useCallback(() => {
    onCancel();
    dispatch(clearInsuranceError());
  }, [dispatch, onCancel]);

  useInsuranceWarningActionSheet({
    handleContinue: insuranceWarningOnContinue,
    handleAddAnotherBenefit: insuranceWarningOnAddAnotherBenefit,
  });

  useEffect(() => {
    trackPageView({
      event: EVENTS.ONBOARDING__INSURANCE_INPUT_MANUAL_VIEWED,
      params: {
        origin,
        is_progyny_rx_view: isProgyny,
      },
    });
  }, [isProgyny, origin, trackPageView]);

  const {
    control,
    formState: { isSubmitted },
    handleSubmit,
    reset,
  } = useForm<Values>({
    defaultValues: getInitialInsuranceFormValues(savedOnboardingInsurance ?? null),
    mode: 'onSubmit',
    resolver: (values) => {
      const errs = validate(values) as Partial<Record<keyof Values, string>>;
      const errors: Record<string, { type: string; message: string }> = {};
      for (const k of Object.keys(errs) as Array<keyof Values>) {
        const msg = errs[k];
        if (msg) errors[k as string] = { type: 'validate', message: msg };
      }
      if (Object.keys(errors).length > 0) {
        return { values: {} as Record<string, never>, errors: errors as FieldErrors<Values> };
      }
      return { values, errors: {} };
    },
  });

  useEffect(() => {
    reset(getInitialInsuranceFormValues(savedOnboardingInsurance ?? null));
  }, [savedOnboardingInsurance, reset]);

  if (isFetching) {
    return (
      <XlPadding>
        <LoadingEmptyState />
      </XlPadding>
    );
  }

  return (
    <SecondaryPage
      headerBackgroundColor={headerBackgroundColor}
      withoutWebFramingElementHeights
      dismissIcon="chevronleft"
      onDismiss={onBack}
      NavBarCenterContent={
        <OnboardingNavBarLogo
          illustrationSrc={headerLogoUrl}
          partnershipName={partnershipName}
        />
      }
      HeaderContent={
        <OnboardingHeader
          title={title}
          link={
            <Link
              onPress={openInsuranceInfoActionSheet}
              center
            >
              Where can I find this?
            </Link>
          }
        />
      }
      buttons={[
        <Button
          label="Cancel"
          onPress={onCancel}
          type="tertiary"
          key="cancel button"
          loading={saving}
        />,
        <Button
          label={addBenefitButtonText}
          onPress={handleSubmit(onSave)}
          loading={saving}
          key="submit button"
        />,
      ]}
      footerPlacementContext="webScreenWithoutPolicyFooter"
    >
      <Card>
        <InsuranceFormFields
          control={control}
          isSubmitted={isSubmitted}
        />
      </Card>
    </SecondaryPage>
  );
};

export default OnboardingInsuranceInputManual;
